"use client";

import { c as _c } from "react/compiler-runtime";
import { AvailabilityEnum, EventEnum } from "@sprawdzonabron/shared";
import { usePathname, useRouter } from "next/navigation";
import { TestIdEnum } from "../../../tests/types";
import { useAnalyticsEvent } from "../../api/hooks/useAnalyticsEvent";
import type { getAvailableFilters } from "../../api/services/getAvailableFilters";
import { CITIES_OPTIONS, CITIES_RANGE_OPTIONS } from "../../constants/cities";
import type { Filters as FiltersType } from "../../features/filters/types";
import { getFilters } from "../../features/filters/utils";
import { URLParamEnum } from "../../features/urlParams/types";
import { selectFilters, selectMobileFiltersOpen, useSelector } from "../../store/selectors";
import { useStoreSlices } from "../../store/slices";
import { omitEmpty } from "../../utils/omitEmpty";
import { CurrencyInput } from "./CurrencyInput";
import { FilterAccordion } from "./FilterAccordion";
import { FiltersSummary } from "./FiltersSummary";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { SingleSelectFilter } from "./SingleSelectFilter";
interface FiltersProps {
  availableFilters: Awaited<ReturnType<typeof getAvailableFilters>>;
}
export const Filters = t0 => {
  const $ = _c(91);
  const {
    availableFilters
  } = t0;
  const {
    setFilter,
    resetFilters
  } = useStoreSlices();
  const filters = useSelector(selectFilters);
  const pathname = usePathname();
  const router = useRouter();
  const {
    priceMin,
    priceMax,
    shop,
    caliber,
    voivodeship,
    available,
    producer,
    city,
    cityRange
  } = filters;
  const mobileFiltersOpen = useSelector(selectMobileFiltersOpen);
  const postEvent = useAnalyticsEvent(EventEnum.FilterSelection);
  let t1;
  if ($[0] !== postEvent || $[1] !== setFilter) {
    t1 = filter => data => {
      postEvent(filter);
      setFilter(filter, data);
    };
    $[0] = postEvent;
    $[1] = setFilter;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const changeWithPostEvent = t1;
  let t2;
  if ($[3] !== changeWithPostEvent || $[4] !== pathname || $[5] !== postEvent || $[6] !== router) {
    t2 = city_0 => {
      if (pathname?.includes("/lokalizacja")) {
        postEvent("city");
        const newFilters = omitEmpty(getFilters(new URLSearchParams(window.location.search), {
          city: city_0
        }));
        const newSearchParams = new URLSearchParams({
          [URLParamEnum.Filters]: JSON.stringify(newFilters)
        });
        router.push(`/?${newSearchParams}`);
        return;
      }
      changeWithPostEvent("city")(city_0);
    };
    $[3] = changeWithPostEvent;
    $[4] = pathname;
    $[5] = postEvent;
    $[6] = router;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onCityChange = t2;
  let t3;
  if ($[8] !== changeWithPostEvent || $[9] !== pathname || $[10] !== postEvent || $[11] !== router) {
    t3 = producer_0 => {
      if (pathname?.includes("/bron/")) {
        postEvent("producer");
        const newFilters_0 = omitEmpty(getFilters(new URLSearchParams(window.location.search), {
          producer: producer_0
        }));
        const newSearchParams_0 = new URLSearchParams({
          [URLParamEnum.Filters]: JSON.stringify(newFilters_0)
        });
        router.push(`/?${newSearchParams_0.toString()}`, {
          scroll: true
        });
        return;
      }
      changeWithPostEvent("producer")(producer_0);
    };
    $[8] = changeWithPostEvent;
    $[9] = pathname;
    $[10] = postEvent;
    $[11] = router;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const onChangeProducer = t3;
  const filtersContainerClasses = `
    fixed inset-0 z-40 bg-black bg-opacity-50 p-2 lg:p-0 lg:static lg:bg-transparent lg:z-auto
    ${mobileFiltersOpen ? "block" : "hidden lg:block"}
  `;
  if (availableFilters.error) {
    let t4;
    if ($[13] !== availableFilters.error.message) {
      t4 = <div className="h-full w-full lg:max-w-xs flex flex-col bg-white dark:bg-slate-900 shadow-lg rounded-lg p-4"><p className="text-sm text-error-600 dark:text-error-400">Błąd podczas pobierania filtrów: {availableFilters.error.message}</p></div>;
      $[13] = availableFilters.error.message;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== filtersContainerClasses || $[16] !== t4) {
      t5 = <div className={filtersContainerClasses}>{t4}</div>;
      $[15] = filtersContainerClasses;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t4;
  if ($[18] !== availableFilters.data) {
    t4 = <div className="rounded-t-xl lg:hidden"><FiltersSummary availableFilters={availableFilters.data} /></div>;
    $[18] = availableFilters.data;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">Filtry</h2>;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== resetFilters) {
    t6 = <div className="hidden lg:flex items-center justify-between p-4">{t5}<button onClick={resetFilters} className="text-[10px] font-medium text-brand-600 hover:text-brand-700 underline">Wyczyść</button></div>;
    $[21] = resetFilters;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== city || $[24] !== onCityChange) {
    t7 = <SingleSelectFilter options={CITIES_OPTIONS} selected={city} onChange={onCityChange} clearable={true} placeholder="Wybierz miasto" id="city" />;
    $[23] = city;
    $[24] = onCityChange;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== changeWithPostEvent) {
    t8 = changeWithPostEvent("cityRange");
    $[26] = changeWithPostEvent;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== cityRange || $[29] !== t8) {
    t9 = <SingleSelectFilter options={CITIES_RANGE_OPTIONS} selected={cityRange} onChange={t8} placeholder={"Zasi\u0119g"} id="range" />;
    $[28] = cityRange;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t7 || $[32] !== t9) {
    t10 = <FilterAccordion title="Obszar:"><div className="flex flex-col gap-2">{t7}{t9}</div></FilterAccordion>;
    $[31] = t7;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== changeWithPostEvent) {
    t11 = changeWithPostEvent("priceMin");
    $[34] = changeWithPostEvent;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== priceMin || $[37] !== t11) {
    t12 = <CurrencyInput placeholder="od" value={priceMin} onChange={t11} />;
    $[36] = priceMin;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-slate-400">-</span>;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== changeWithPostEvent) {
    t14 = changeWithPostEvent("priceMax");
    $[40] = changeWithPostEvent;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== priceMax || $[43] !== t14) {
    t15 = <CurrencyInput placeholder="do" value={priceMax} onChange={t14} />;
    $[42] = priceMax;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t12 || $[46] !== t15) {
    t16 = <FilterAccordion title="Cena:"><div className="flex gap-2 items-center">{t12}{t13}{t15}</div></FilterAccordion>;
    $[45] = t12;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== changeWithPostEvent) {
    t17 = data_0 => changeWithPostEvent("available")(data_0 as AvailabilityEnum[]);
    $[48] = changeWithPostEvent;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== available || $[51] !== availableFilters.data.availability || $[52] !== t17) {
    t18 = <FilterAccordion title={"Dost\u0119pno\u015B\u0107:"}><MultiSelectFilter title={"Dost\u0119pno\u015B\u0107"} options={availableFilters.data.availability} selected={available} onChange={t17} /></FilterAccordion>;
    $[50] = available;
    $[51] = availableFilters.data.availability;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const t19 = availableFilters.data.calibers;
  let t20;
  if ($[54] !== changeWithPostEvent) {
    t20 = changeWithPostEvent("caliber");
    $[54] = changeWithPostEvent;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== availableFilters.data.calibers || $[57] !== caliber || $[58] !== t20) {
    t21 = <FilterAccordion title="Kaliber:"><MultiSelectFilter title="Kaliber" options={t19} selected={caliber} onChange={t20} /></FilterAccordion>;
    $[56] = availableFilters.data.calibers;
    $[57] = caliber;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== availableFilters.data.producers || $[61] !== onChangeProducer || $[62] !== producer) {
    t22 = <FilterAccordion title="Producent:"><MultiSelectFilter title="Producent" options={availableFilters.data.producers} selected={producer} onChange={onChangeProducer} /></FilterAccordion>;
    $[60] = availableFilters.data.producers;
    $[61] = onChangeProducer;
    $[62] = producer;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const t23 = availableFilters.data.voivodeship;
  let t24;
  if ($[64] !== changeWithPostEvent) {
    t24 = changeWithPostEvent("voivodeship");
    $[64] = changeWithPostEvent;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== availableFilters.data.voivodeship || $[67] !== t24 || $[68] !== voivodeship) {
    t25 = <FilterAccordion title={"Wojew\xF3dztwo:"}><MultiSelectFilter title={"Wojew\xF3dztwo"} options={t23} selected={voivodeship} onChange={t24} /></FilterAccordion>;
    $[66] = availableFilters.data.voivodeship;
    $[67] = t24;
    $[68] = voivodeship;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  const t26 = availableFilters.data.shops;
  let t27;
  if ($[70] !== changeWithPostEvent) {
    t27 = changeWithPostEvent("shop");
    $[70] = changeWithPostEvent;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== availableFilters.data.shops || $[73] !== shop || $[74] !== t27) {
    t28 = <FilterAccordion title="Sklep:" rounded={true}><MultiSelectFilter title="Sklep" options={t26} selected={shop} onChange={t27} /></FilterAccordion>;
    $[72] = availableFilters.data.shops;
    $[73] = shop;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== t10 || $[77] !== t16 || $[78] !== t18 || $[79] !== t21 || $[80] !== t22 || $[81] !== t25 || $[82] !== t28) {
    t29 = <div className="overflow-y-auto scrollbar-thin rounded-xl">{t10}{t16}{t18}{t21}{t22}{t25}{t28}</div>;
    $[76] = t10;
    $[77] = t16;
    $[78] = t18;
    $[79] = t21;
    $[80] = t22;
    $[81] = t25;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== t29 || $[85] !== t4 || $[86] !== t6) {
    t30 = <aside className="w-full h-full lg:h-auto lg:min-w-[280px] lg:max-w-xs flex flex-col bg-white dark:bg-slate-850 rounded-xl shadow-lg">{t4}{t6}{t29}</aside>;
    $[84] = t29;
    $[85] = t4;
    $[86] = t6;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== filtersContainerClasses || $[89] !== t30) {
    t31 = <div data-testid={TestIdEnum.Filters} className={filtersContainerClasses}>{t30}</div>;
    $[88] = filtersContainerClasses;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  return t31;
};